/** Semântica pura do recorte e da classificação da aba Orçamentos. */
import type { QuoteData } from '@/components/reports/QuotesReport';

export const QUOTE_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pendente' },
  { value: 'approved', label: 'Aprovado' },
  { value: 'completed', label: 'Concluído' },
  { value: 'cancelled', label: 'Cancelado' },
  { value: 'rejected', label: 'Recusado' },
] as const;

export const QUOTES_REPORT_SORT_FIELDS = [
  { value: 'date', label: 'Data', serverColumn: 'created_at' },
  { value: 'total', label: 'Valor', serverColumn: 'total' },
  { value: 'client', label: 'Cliente', serverColumn: 'client_name' },
  { value: 'status', label: 'Status', serverColumn: 'status' },
] as const;

export type QuotesReportSortField = (typeof QUOTES_REPORT_SORT_FIELDS)[number]['value'];
export type SortDirection = 'asc' | 'desc';

export interface QuotesReportSort {
  field: QuotesReportSortField;
  direction: SortDirection;
}

export const DEFAULT_QUOTES_REPORT_SORT: QuotesReportSort = {
  field: 'date',
  direction: 'desc',
};

function compareText(a: unknown, b: unknown): number {
  return String(a ?? '').localeCompare(String(b ?? ''), 'pt-BR', { sensitivity: 'base' });
}

function compareNumber(a: unknown, b: unknown): number {
  const left = Number(a ?? 0);
  const right = Number(b ?? 0);
  return left - right;
}

/** Ordenação estável usada pelo cache offline; o servidor recebe as mesmas colunas. */
export function sortQuoteRows(
  rows: readonly QuoteData[],
  sort: QuotesReportSort,
): QuoteData[] {
  const direction = sort.direction === 'asc' ? 1 : -1;
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const left = sort.field === 'date'
        ? a.row.created_at
        : sort.field === 'total'
          ? a.row.total
          : sort.field === 'client'
            ? a.row.client_name
            : a.row.status;
      const right = sort.field === 'date'
        ? b.row.created_at
        : sort.field === 'total'
          ? b.row.total
          : sort.field === 'client'
            ? b.row.client_name
            : b.row.status;
      const leftMissing = left == null || left === '';
      const rightMissing = right == null || right === '';
      if (leftMissing !== rightMissing) return leftMissing ? 1 : -1;
      let compared = 0;
      if (sort.field === 'total') compared = compareNumber(left, right);
      else compared = compareText(left, right);
      return compared * direction || a.index - b.index;
    })
    .map(({ row }) => row);
}

export function quoteSortServerColumn(field: QuotesReportSortField): string {
  return QUOTES_REPORT_SORT_FIELDS.find((option) => option.value === field)?.serverColumn
    ?? 'created_at';
}
